import { io } from "socket.io-client";
import { Socket } from "socket.io-client/build/esm/socket";
import { AuthService } from "@service/auth.service";
import { AudioMessageStream } from "@models/audio-message-stream";

export enum StreamState {
  Transcription = "Transcription",
  Processing = "Processing",
  Streaming = "Streaming",
  Completed = "Completed",
}

const BASE_API_URL = process.env.REACT_APP_API_URL as string;

export type CompanionResponsePayload =
  | string
  | { text: string; streamSeq?: number };

export class CompanionStreamingService {
  private readonly socket: Socket;
  private readonly authService = AuthService.instance;
  private readonly connected: Promise<void>;

  private readonly onResponseHandler = (payload: CompanionResponsePayload) => {
    if (typeof payload === "string") {
      this.onResponse?.(payload, undefined);
      return;
    }
    this.onResponse?.(payload.text, payload.streamSeq);
  };

  private readonly onStateChangeHandler = (state: StreamState) => {
    this.onStateChange?.(state);
  };

  constructor(
    private readonly onResponse: (chunk: string, streamSeq?: number) => void,
    private readonly onStateChange: (state: StreamState) => void,
  ) {
    this.socket = io(`${BASE_API_URL}/companion`, {
      transports: ["websocket"],
      auth: async (cb) => {
        const token = await this.authService.getAccessToken();
        cb({ token });
      },
    });

    this.connected = new Promise<void>((resolve) => {
      this.socket.on("connect", resolve);
    });

    this.socket.on("response", this.onResponseHandler);

    this.socket.on("stateChange", this.onStateChangeHandler);
  }

  async sendMessage(message: AudioMessageStream): Promise<void> {
    await this.connected;

    const { audio, ...data } = message;

    this.socket.emit("message", data, audio);
  }

  destroy(): void {
    this.socket.off("response", this.onResponseHandler);
    this.socket.off("stateChange", this.onStateChangeHandler);
    this.socket.disconnect();
  }
}
